/**
 * Bonds Service
 * Handles all bond-related API calls
 */

import httpClient, { extractData, extractPaginatedData } from './http-client';
import {
  ApiResponse,
  Bond,
  BondDailyData,
  BondQueryParams,
  PaginatedResponse,
  FilterOptions,
} from '../types';

class BondsService {
  /**
   * Get all bonds with filtering and pagination
   */
  async getAll(params?: BondQueryParams): Promise<PaginatedResponse<Bond>> {
    const response = await httpClient.get<ApiResponse<Bond[]>>('/bonds', { params });
    return extractPaginatedData(response);
  }

  /**
   * Get bond by ID
   */
  async getById(id: string): Promise<Bond> {
    const response = await httpClient.get<ApiResponse<Bond>>(`/bonds/${id}`);
    return extractData(response);
  }

  /**
   * Get bond by Bloomberg code
   */
  async getByCode(bloombergCode: string): Promise<Bond> {
    const response = await httpClient.get<ApiResponse<Bond>>(
      `/bonds/code/${encodeURIComponent(bloombergCode)}`
    );
    return extractData(response);
  }

  /**
   * Get historical data for a bond
   */
  async getHistory(
    bondId: string,
    options?: { days?: number; dateFrom?: string; dateTo?: string }
  ): Promise<BondDailyData[]> {
    const response = await httpClient.get<ApiResponse<BondDailyData[]>>(
      `/bonds/${bondId}/history`,
      { params: options }
    );
    return extractData(response);
  }

  /**
   * Get performance metrics for a bond
   */
  async getPerformance(bondId: string): Promise<any> {
    const response = await httpClient.get<ApiResponse<any>>(
      `/bonds/${bondId}/performance`
    );
    return extractData(response);
  }

  /**
   * Get performance attribution for a bond
   */
  async getAttribution(
    bondId: string,
    period: '1D' | 'MTD' | '3MTD' | 'YTD' = 'MTD'
  ): Promise<any> {
    const response = await httpClient.get<ApiResponse<any>>(
      `/bonds/${bondId}/attribution`,
      { params: { period } }
    );
    return extractData(response);
  }

  /**
   * Get richness metrics for a bond
   */
  async getRichness(bondId: string): Promise<any> {
    const response = await httpClient.get<ApiResponse<any>>(
      `/bonds/${bondId}/richness`
    );
    return extractData(response);
  }

  /**
   * Get available filter options
   */
  async getFilterOptions(): Promise<FilterOptions> {
    const response = await httpClient.get<ApiResponse<FilterOptions>>('/bonds/filters');
    return extractData(response);
  }
}

export const bondsService = new BondsService();
export default bondsService;
